import { TextareaHTMLAttributes, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { FormLabel } from "../FormLabel";
import ReactTooltip from "react-tooltip";
import FormikFieldError from "../FormikFieldError";
import { getInputClasses, getInputWrapperClasses } from "./inputStyles";
import InfoTooltip from "newui/components/InfoTooltip";
import { cn } from "utils/helpers";

export interface BaseTextAreaWithLabelProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'> {
	className?: string;
	errorText?: string;
	flexGrow?: number;
	guideLine?: string;
	hasError?: boolean;
	hint?: string;
	label?: string;
	maxHeight?: number;
	maxLength?: number;
	noMargin?: boolean;
	placeholder?: string;
	required?: boolean;
	resize?: 'none' | 'vertical' | 'horizontal' | 'both';
	rows?: number;
	showCharacterCount?: boolean;
	testId?: string;
	textAreaClassName?: string;
	value?: string;
	onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
}

export const TextAreaWithLabel: React.FC<BaseTextAreaWithLabelProps> = ({
	label,
	required,
	placeholder,
	className = "",
	textAreaClassName = "",
	errorText,
	hasError = false,
	guideLine,
	onChange,
	testId,
	flexGrow,
	noMargin,
	value,
	disabled,
	id,
	name,
	hint,
	maxHeight,
	maxLength,
	showCharacterCount = true,
	resize = 'vertical',
	rows = 4,
	...restProps
}) => {
	const [characterCount, setCharacterCount] = useState(value?.toString().length || 0);
	const [showOverflow, setShowOverflow] = useState(false);

	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const wrapperRef = useRef<HTMLDivElement>(null);
	// Flag to distinguish programmatic resize (adjustHeight) from user drag resize
	const isAdjustingRef = useRef(false);

	const hasInputError = !!errorText || hasError;

	useEffect(() => {
		const ta = textareaRef.current;
		const wrap = wrapperRef.current;
		if (!ta || !wrap) {
			return;
		}

		const observer = new ResizeObserver(() => {
			// Skip when adjustHeight is programmatically setting the height
			if (isAdjustingRef.current) {
				return;
			}
			// User manually resized — show scrollbars if content overflows the wrapper
			setShowOverflow(wrap.clientHeight < ta.scrollHeight);
		});

		observer.observe(wrap);

		return () => observer.disconnect();
	}, []);

	const adjustHeight = useCallback(() => {
		const ta = textareaRef.current;
		const wrap = wrapperRef.current;
		if (!ta || !wrap) {
			return;
		}

		isAdjustingRef.current = true;

		ta.style.height = 'auto';
		const scrollH = ta.scrollHeight;
		ta.style.height = '';

		const finalH = maxHeight ? Math.min(scrollH, maxHeight) : scrollH;
		wrap.style.height = `${finalH}px`;
		setShowOverflow(maxHeight ? scrollH > maxHeight : false);

		// Reset flag after the browser has processed the resize
		requestAnimationFrame(() => {
			isAdjustingRef.current = false;
		});
	}, [maxHeight]);

	useLayoutEffect(() => {
		adjustHeight();
	}, [value, adjustHeight]);

	const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const newValue = e.currentTarget.value;
		setCharacterCount(newValue.length);

		if (onChange) {
			onChange(e);
		}
	};

	const remainingCharacters = maxLength ? maxLength - characterCount : 0;

	return (
		<div
			className={getInputWrapperClasses(hasInputError, noMargin, className)}
			style={{ flexGrow: flexGrow || 0 }}
		>
			{label && (
				<FormLabel
					htmlFor={id || name}
					label={label}
					required={required}
				>
					{guideLine && (
						<div className={"inline-block"}>
							<InfoTooltip tooltipText={guideLine} />
						</div>
					)}
				</FormLabel>
			)}

			{hint && (
				<p className="-mt-2 mb-2 text-sm text-gray-500">{hint}</p>
			)}

			<div className="relative">
				<div
					ref={wrapperRef}
					className={cn(` overflow-hidden ${resize === 'none' ? 'resize-none' : 'resize-y'}`)}
					style={{
						minHeight: `${rows * 1.5 + 2}rem`,
						maxHeight: maxHeight ? `${maxHeight}px` : undefined,
					}}
				>
					<textarea
						ref={textareaRef}
						id={id}
						name={name}
						data-testid={testId}
						placeholder={placeholder}
						onChange={handleChange}
						className={getInputClasses(hasInputError, `resize-none h-full ${textAreaClassName}`)}
						style={{ overflowY: showOverflow ? 'auto' : 'hidden' }}
						value={value}
						disabled={disabled}
						maxLength={maxLength}
						{...restProps}
					/>
				</div>

				{showCharacterCount && maxLength && (
					<div className="text-right text-xs text-gray-500 bg-white px-1">
						{remainingCharacters} character{remainingCharacters !== 1 ? 's' : ''} remaining
					</div>
				)}
			</div>

			<FormikFieldError text={errorText} />
		</div>
	);
};
